import React, { useCallback, useMemo, useState } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { AssetContainer, ImageAsset, useAsset } from '@techstyle/react-assets';
import { TextLink, useBreakpoint } from '@techstyle/react-components';
import {
  defineMessages,
  FormattedMessage,
  useIntl,
} from '@techstyle/react-intl';

import { mobile } from '../../styles';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import { getProductCategoryTypes } from '../../utils/productDetail';
import BraCalculator from '../BraCalculator';
import BraCalculatorDrawer from '../BraCalculator/BraCalculatorDrawer';
import { Component as SizeGuideModal } from '../SizeGuideModal';
import StyleContext from '../StyleContext/StyleContext';

const defaultCategories = config.get('public.productBrowser.sections');

const Wrapper = styled.span``;

const SizeGuideButton = styled(TextLink)`
  ${({ theme }) => theme.links.variants.navigationSecondary}
  color: ${({ theme }) => theme.colors.grey};

  float: ${props => (props.shouldShowBottomSizeAndFitGuide ? 'none' : 'right')};
  flex: 0 0 auto;
  position: relative;
  padding: 0px 0px 3px;
  background: transparent;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.4s ease-out;

  &:hover {
    &::before {
      width: 100%;
      transition: all 0.2s ease-out;
    }
  }
`;

const SpecificProductSizeGuideButton = styled(SizeGuideButton)`
  float: none;
`;

const SizeGuideImage = styled(ImageAsset)`
  display: block;
  margin: 0 auto;
  width: 100%;
  max-width: 800px;

  ${mobile`
    max-width: 375px;
  `};
`;

const BraCalculatorModalWrapper = styled.div`
  max-width: 600px;
  padding: 48px 0;
`;

const linkNames = defineMessages({
  undieLinkGuide: {
    id: 'site_pdp.size_undie_guide_link',
    defaultMessage: 'Undie Guide',
  },
  braLinkGuide: {
    id: 'site_pdp.size_bra_guide_link',
    defaultMessage: 'Bra Guide',
  },
});

const ProductDetailSizeLinks = ({
  isAllGenderProduct,
  isProductSet,
  product,
  shouldShowBottomSizeAndFitGuide,
  handleShopMySize: handleShopMySizeSubmit,
}) => {
  const { formatMessage } = useIntl();
  const [showModal, setShowModal] = useState(false);
  const [showSpecificProductSizeModal, setSpecificProductSizeShowModal] =
    useState(false);

  const { 'show-bra-calculator-page-and-pdp-modal': shouldShowBraCalculator } =
    useLDFlags();

  const assetContainerDesktopSizeGuide = useAsset('pdp_desktop_size_guide', {
    multi: true,
  });
  const { data } = assetContainerDesktopSizeGuide;
  const assetsDesktopSizeGuide = data ? data.assets : [];
  const { isMobile } = useBreakpoint();
  const [showBraCalculatorDrawer, setShowBraCalculatorDrawer] = useState(false);

  const productType = useMemo(() => {
    if (product) {
      const {
        product_category_id_list: productCategoryIdList,
        tag_id_list: tagIdList,
      } = product;
      const { isBra, isBralette, isUndies } = getProductCategoryTypes(
        productCategoryIdList,
        tagIdList
      );
      return ((isBra || isBralette) && 'bra') || (isUndies && 'undies');
    }
  }, [product]);

  const handleShopMySize = useCallback(
    (...args) => {
      if (!isMobile) {
        setShowModal(false);
      }
      return handleShopMySizeSubmit(...args);
    },
    [handleShopMySizeSubmit, setShowModal]
  );

  const handleSizeGuideModal = () => {
    setShowModal(!showModal);
  };

  const handleSpecificProductSizeGuideModal = () => {
    setSpecificProductSizeShowModal(!showSpecificProductSizeModal);
  };

  const renderSetSizeGuide = () => {
    return product.products_in_set.map(setItem => {
      return renderProductByCategory(setItem);
    });
  };

  const renderSizeGuide = () => {
    return renderProductByCategory(product);
  };

  const renderProductByCategory = productDetail => {
    const { product_category_id_list: productCategoryIdList } = productDetail;

    const productCategoryIdListReversed = [...productCategoryIdList].reverse();
    let imageAsset = null;

    productCategoryIdListReversed.some(productCategoryId => {
      const productImageAsset = assetsDesktopSizeGuide.find(asset => {
        let {
          customVars: { productCategoryID: assetProductCategoryIDs },
        } = asset.options;

        if (!assetProductCategoryIDs) {
          return false;
        }

        if (typeof assetProductCategoryIDs === 'number') {
          assetProductCategoryIDs = assetProductCategoryIDs.toString();
        }

        assetProductCategoryIDs = assetProductCategoryIDs
          .replace(/\s/g, '')
          .split(',');

        return assetProductCategoryIDs.includes(productCategoryId.toString());
      });

      if (productImageAsset) {
        imageAsset = productImageAsset;
      }

      return imageAsset;
    });

    if (!imageAsset) {
      return null;
    }

    return <SizeGuideImage asset={imageAsset} lazyLoad />;
  };

  if (!product) {
    return null;
  }

  const accessoriesCategoryId =
    defaultCategories.accessories.defaultProductCategoryIds[0];

  const isAccessories = product.product_category_id_list.includes(
    accessoriesCategoryId
  );

  const renderSpecificProductLinks = () => {
    if (!productType) {
      return null;
    }

    let assetContainer = null;
    let label = null;
    const { undieLinkGuide, braLinkGuide } = linkNames;
    switch (productType) {
      case 'bra':
        assetContainer = 'pdp_bra_guide';
        label = braLinkGuide;
        break;
      case 'undies':
        assetContainer = 'pdp_undie_guide';
        label = undieLinkGuide;
        break;
    }
    const link = (
      <Wrapper>
        <AssetContainer name={assetContainer} multi>
          {assets => {
            if (!assets.data || !assets.data.length) {
              return null;
            }
            const imageAsset = assets.data.find(asset => {
              const {
                customVars: { tagIds },
              } = asset.options;
              if (!tagIds || tagIds === '') {
                return null;
              }
              const tagIdValue = tagIds.toString();
              const assetTagIds = tagIdValue
                .split(',')
                .map(tagId => parseInt(tagId));
              const { tag_id_list: tagIdList } = product;

              return tagIdList.some(tagId => assetTagIds.includes(tagId));
            });

            if (!imageAsset) {
              return null;
            }

            return (
              <Wrapper>
                <SpecificProductSizeGuideButton
                  onClick={handleSpecificProductSizeGuideModal}
                  shouldShowBottomSizeAndFitGuide={
                    shouldShowBottomSizeAndFitGuide
                  }
                >
                  <FormattedMessage {...label} />
                </SpecificProductSizeGuideButton>{' '}
                |
                <SizeGuideModal
                  onClose={handleSpecificProductSizeGuideModal}
                  isOpen={showSpecificProductSizeModal}
                  titleId="specific-size-guide-modal"
                  isSpecificProduct
                >
                  <SizeGuideImage
                    asset={imageAsset}
                    id="specific-size-guide-modal"
                    lazyLoad
                  />
                </SizeGuideModal>
              </Wrapper>
            );
          }}
        </AssetContainer>
      </Wrapper>
    );
    return link;
  };


  if (isMobile && productType === 'bra' && shouldShowBraCalculator) {
    return (
      <>
        <SizeGuideButton onClick={() => setShowBraCalculatorDrawer(true)}>
          <FormattedMessage
            id="site_pdp.size_guide_link"
            defaultMessage="Size guide"
          />
        </SizeGuideButton>
        <BraCalculatorDrawer
          isOpen={showBraCalculatorDrawer}
          setShowBraCalculatorDrawer={setShowBraCalculatorDrawer}
          handleShopMySize={handleShopMySize}
        />
      </>
    );
  }

  return (
    <>
      {!isAccessories &&
        shouldShowBottomSizeAndFitGuide &&
        renderSpecificProductLinks()}
      {!isAccessories && (
        <SizeGuideButton
          onClick={handleSizeGuideModal}
          shouldShowBottomSizeAndFitGuide={shouldShowBottomSizeAndFitGuide}
          data-autotag="guideSizeModal"
        >
          {isAllGenderProduct ? (
            <FormattedMessage
              id="site_pdp._youniversal_size_guide_link"
              defaultMessage="What's My Size?"
            />
          ) : (
            <FormattedMessage
              id="site_pdp.size_guide_link"
              defaultMessage="Size guide"
            />
          )}
        </SizeGuideButton>
      )}

      {(productType !== 'bra' || !shouldShowBraCalculator) && (
        <SizeGuideModal
          onClose={handleSizeGuideModal}
          isOpen={showModal}
          title={formatMessage({
            id: 'site_pdp.size_guide_link',
            defaultMessage: 'Size guide',
          })}
        >
          {isProductSet ? renderSetSizeGuide() : renderSizeGuide()}
        </SizeGuideModal>
      )}
      {productType === 'bra' && shouldShowBraCalculator && (
        <SizeGuideModal
          onClose={handleSizeGuideModal}
          isOpen={showModal}
          title={formatMessage({
            id: 'site_pdp.size_guide_link',
            defaultMessage: 'Size guide',
          })}
          modalMaxHeight="90vh"
        >
          {isProductSet ? (
            renderSetSizeGuide()
          ) : (
            <StyleContext context="braCalculator">
              <BraCalculatorModalWrapper>
                <BraCalculator
                  handleShopMySize={handleShopMySize}
                  activeTabFromProps="sizeChart"
                  reverseTabs
                />
              </BraCalculatorModalWrapper>
            </StyleContext>
          )}
        </SizeGuideModal>
      )}
    </>
  );
};

ProductDetailSizeLinks.propTypes = {
  isAllGenderProduct: PropTypes.bool,
  isProductSet: PropTypes.bool,
  product: PropTypes.object,
  shouldShowBottomSizeAndFitGuide: PropTypes.bool,
};

ProductDetailSizeLinks.SizeGuideButton = SizeGuideButton;

export default ProductDetailSizeLinks;
